import { useEffect } from "react";
import type { ExportKind, ExportOut, ExportStatus } from "../api/client";
import { t, type MessageKey } from "../i18n/t";

/** How often the export lists are refetched while an export is queued or running. */
export const EXPORT_POLL_MS = 4000;

const KIND_KEYS: Record<ExportKind, MessageKey> = {
  pdf: "exports.kindPdf",
  xlsx: "exports.kindXlsx",
};

const STATUS_KEYS: Record<ExportStatus, MessageKey> = {
  queued: "exports.statusQueued",
  running: "exports.statusRunning",
  succeeded: "exports.statusSucceeded",
  failed: "exports.statusFailed",
};

export function exportKindLabel(kind: ExportKind): string {
  return t(KIND_KEYS[kind]);
}

export function exportStatusLabel(status: ExportStatus): string {
  return t(STATUS_KEYS[status]);
}

export function isPendingExport(e: ExportOut): boolean {
  return e.status === "queued" || e.status === "running";
}

/**
 * Same-origin download URLs. A plain link keeps the session cookie, and the browser takes the file
 * name from the server's `Content-Disposition` header.
 */
export const exportDownloadUrl = {
  org: (orgId: string, exportId: string) => `/api/orgs/${orgId}/exports/${exportId}/download`,
  admin: (exportId: string) => `/api/admin/exports/${exportId}/download`,
};

/**
 * Refetch `reload` every `EXPORT_POLL_MS` while any export in `exports` is pending. Each new result
 * schedules the next fetch, so polls never overlap; it stops as soon as nothing is pending.
 */
export function usePollPendingExports(
  exports: readonly ExportOut[] | undefined,
  reload: () => void,
): void {
  const pending = !!exports?.some(isPendingExport);
  useEffect(() => {
    if (!pending) return;
    const id = window.setTimeout(reload, EXPORT_POLL_MS);
    return () => window.clearTimeout(id);
  }, [pending, exports, reload]);
}
